import { computed, signal } from '@preact/signals';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'failed';

// Connection state signals
export const connectionStatus = signal<ConnectionStatus>('disconnected');
export const connectionError = signal<string | null>(null);
export const reconnectAttempts = signal<number>(0);
export const serverUrl = signal<string>('');
export const gameReady = signal<boolean>(false);

// Internal readiness flags - game is ready only when both are true
let _welcomeReceived = false;
let _timeSynced = false;

// Network latency signals (updated by NetworkClient)
export const latencyRtt = signal<number>(0);
export const latencyJitter = signal<number>(0);

// Derived state
export const isConnecting = computed(() => connectionStatus.value === 'connecting');
export const isConnected = computed(() => connectionStatus.value === 'connected');
export const isFailed = computed(() => connectionStatus.value === 'failed');

// Actions
export const setConnecting = (): void => {
  connectionStatus.value = 'connecting';
  connectionError.value = null;
};

export const setConnected = (): void => {
  connectionStatus.value = 'connected';
  connectionError.value = null;
  reconnectAttempts.value = 0;
};

const checkGameReady = (): void => {
  if (_welcomeReceived && _timeSynced) {
    gameReady.value = true;
  }
};

export const markWelcomeReceived = (): void => {
  _welcomeReceived = true;
  checkGameReady();
};

export const markTimeSynced = (): void => {
  _timeSynced = true;
  checkGameReady();
};

export const setDisconnected = (): void => {
  connectionStatus.value = 'disconnected';
};

export const setFailed = ({ error }: { error: string }): void => {
  connectionStatus.value = 'failed';
  connectionError.value = error;
};

export const incrementReconnectAttempts = (): void => {
  reconnectAttempts.value = reconnectAttempts.value + 1;
};

// Reset only reconnect attempts (used after successful connection)
export const resetReconnectAttempts = (): void => {
  reconnectAttempts.value = 0;
};

// Full reset - sets status to disconnected (used when explicitly disconnecting)
export const resetConnection = (): void => {
  connectionStatus.value = 'disconnected';
  connectionError.value = null;
  reconnectAttempts.value = 0;
  gameReady.value = false;
  _welcomeReceived = false;
  _timeSynced = false;
};

export const setServerUrl = ({ url }: { url: string }): void => {
  serverUrl.value = url;
};

export const updateLatency = ({ rtt, jitter }: { rtt: number; jitter: number }): void => {
  latencyRtt.value = rtt;
  latencyJitter.value = jitter;
};
